import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useState } from 'react'

import {
  getInformationSchema,
  getPasswordSchema,
  NotificationChannel,
  useVerifyRegisterInformationOTPMutation,
  UserTypeEnum,
  useFinishRegisterMutation,
  Auth
} from '@data/users'

import type { RegisterPayload } from '@data/users'
import { useNotification } from '.'

export const useRegisterStepForm = () => {
  const defaultValues: Partial<RegisterPayload> = {
    first_name: '',
    company: '',
    company_id: 0,
    birth_date: '',
    employee_id: '',
    email: '',
    password: '',
    confirm_password: '',
  }

  const [activeStep, setActiveStep] = useState(0)
  const [loadingForm, setLoadingForm] = useState(false)
  const verifyRegisterOtp = useVerifyRegisterInformationOTPMutation()
  const finalRegisterMutation = useFinishRegisterMutation()
  const notify = useNotification();

  const { __ } = useTranslator()

  const getStepSchema = (step: number): yup.ObjectSchema<any> => {
    switch (step) {
      case 0:
        return getInformationSchema(__, UserTypeEnum.Admin)
      case 1:
        return getPasswordSchema(__)
      default:
        return yup.object()
    }
  }

  const getStepContent = () => {
    const { email, phone, channel } = methods.control._formValues
    let descriptionCodeVerifyStep = ''

    if (channel === NotificationChannel.Telegram || channel === NotificationChannel) {
      descriptionCodeVerifyStep = __(
        'Enter the verification code we just sent to your telegram phone number: {{phone}}',
        {
          phone
        }
      )
    } else {
      descriptionCodeVerifyStep = __('Enter the verification code we just sent to your email: {{email}}', {
        email
      })
    }

    switch (activeStep) {
      case 1:
        return {
          title: __('Code Verification'),
          description: descriptionCodeVerifyStep
        }
      case 2:
        return {
          title: __('Set New Passwords'),
          description: __('Enter password and enter authentication password')
        }
      default:
        return {
          title: __('Internal User Information'),
          description: __('Fill in basic internal user information')
        }
    }
  }

  const methods = useForm<RegisterPayload>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(getStepSchema(activeStep))
  })

  const handleNext = async () => {
    setLoadingForm(true)
    const isValid = await methods.trigger()
    const payload = methods.control._formValues as RegisterPayload

    if (isValid) {
      if (activeStep === 0) {
        setActiveStep(prev => prev + 1)
      }

      if (activeStep === 1) {
        await verifyRegisterOtp.mutateAsync(payload, {
          onSuccess({ data }) {
            if (data) {
              setActiveStep(prev => prev + 1)
              Auth.handleLoginSuccess(data);

              setTimeout(() => {
                window.location.href = '/cms'
              }, 2000)
            }
          },
          onError:(error) => {
            notify.error(error.message);
          }
        })
      }
    }

    setLoadingForm(false)
  }

  const handleBack = () => {
    setActiveStep(prev => prev - 1)
  }

  const handleSubmit = async (data: RegisterPayload) => {
    await finalRegisterMutation.mutateAsync(data, {
      onSuccess: () => {
        window.location.href = '/cms/waiting-confirm'
      }
    })
  }

  return {
    methods,
    activeStep,
    setActiveStep,
    loadingForm,
    getStepContent,
    setLoadingForm,
    handleNext,
    handleBack,
    handleSubmit: methods.handleSubmit(handleSubmit),
    isStepValid: methods.formState.isValid
  }
}
